import { TextClassContext } from '@/ui/core/text'
import type { TextRef } from '@/ui/primitives/types'
import { cn } from '@/ui/utils'
import { forwardRef } from 'react'
import type { ElementRef, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { ActivityIndicator, Pressable } from 'react-native'
import { Text as RNText, View } from 'react-native'
import { ButtonArrow } from './button.arrow'
import { buttonStyles, buttonTextStyles } from './button.styles'
import type {
	ButtonProps,
	ButtonTextProps,
	ButtonVariant,
	DisabledVariant,
	SizeVariant,
} from './button.types'

type ButtonContext = {
	variant: ButtonVariant
	size: SizeVariant
	disabled: DisabledVariant
	isLoading: boolean
}

//@ts-ignore
const VariantToButtonArrowMap: Record<ButtonVariant, string> = {
	primary: '#F8EDDF',
	secondary: '#F8EDDF',
	ghost: '#F8EDDF',
	ghostBorderless: '#F8EDDF',
	tertiary: '#106A5C',
}

//@ts-ignore
const VariantToButtonLoaderMap: Record<ButtonVariant, string> = {
	primary: 'white',
	secondary: '#106A5C',
	ghost: '#106A5C',
	ghostBorderless: '#106A5C',
	tertiary: '#106A5C',
}

const ButtonContext = createContext<ButtonContext>({
	variant: 'primary',
	size: 'default',
	disabled: false,
	isLoading: false,
})

const Button = forwardRef<ElementRef<typeof Pressable>, ButtonProps>(
	(
		{
			className,
			variant = 'primary',
			size = 'default',
			disabled = false,
			withArrow = false,
			children,
			isLoading = false,
			...props
		},
		ref
	) => (
		<TextClassContext.Provider value={cn(buttonTextStyles({ variant, size, disabled }))}>
			<ButtonContext.Provider value={{ variant, size, disabled, isLoading }}>
				<Pressable
					ref={ref}
					role="button"
					className={cn(buttonStyles({ variant, size, disabled, withArrow }), className)}
					{...props}
				>
					{isLoading && (
						<ActivityIndicator
							color={VariantToButtonLoaderMap[variant]}
							className="absolute left-0 right-0"
						/>
					)}
					{children as ReactNode}
					{withArrow && (
						<View className={isLoading ? 'invisible' : 'visible'}>
							<ButtonArrow fill={VariantToButtonArrowMap[variant]} />
						</View>
					)}
				</Pressable>
			</ButtonContext.Provider>
		</TextClassContext.Provider>
	)
)

const ButtonText = forwardRef<TextRef, ButtonTextProps>(
	({ className, children, spacing, ...props }, ref) => {
		const { variant, size, disabled, isLoading } = useContext(ButtonContext)

		return (
			<RNText
				ref={ref}
				className={cn(
					buttonTextStyles({ variant, size, disabled, spacing, isLoading }),
					className
				)}
				{...props}
			>
				{children}
			</RNText>
		)
	}
)

export { Button, ButtonText }
